import { type ReactElement, type ForwardedRef } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useMeasure, useIsMobileDevice } from '@hivebrite/react-hooks';

import { type DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

interface Context {
  centerRef: ForwardedRef<Element>;
  centerWidth: number;
  centerWrapperRef: ForwardedRef<Element>;
  centerWrapperWidth: number;
  documentRef: ForwardedRef<Element>;
  documentWidth: number;
  isMobile: boolean;
  itemDragging?: DocumentItem;
}

interface LayoutProviderChildrenProps {
  centerRef: Context['centerRef'];
  centerWrapperRef: Context['centerWrapperRef'];
  documentRef: Context['documentRef'];
}

export interface LayoutProviderProps {
  children: ({
    centerRef,
    centerWrapperRef,
    documentRef,
  }: LayoutProviderChildrenProps) => ReactElement;
  itemDragging?: Context['itemDragging'];
}

const LayoutContext = createContext<Context | undefined>(undefined);

export const LayoutProvider = ({
  children,
  itemDragging,
}: LayoutProviderProps) => {
  const [centerRef, { width: centerWidth }] = useMeasure();
  const [centerWrapperRef, { width: centerWrapperWidth }] = useMeasure();
  const [documentRef, { height: documentHeight, width: documentWidth }] =
    useMeasure();

  const isMobileDevice = useIsMobileDevice();

  const breakpoint = useBreakpoint();

  const isMobile = isMobileDevice || breakpoint === 'm' || breakpoint === 's';

  const value = useMemo(
    () => ({
      centerRef,
      centerWidth,
      centerWrapperRef,
      centerWrapperWidth,
      documentRef,
      documentHeight,
      documentWidth,
      itemDragging,
      isMobile,
    }),
    [
      centerRef,
      centerWidth,
      centerWrapperRef,
      centerWrapperWidth,
      documentRef,
      documentHeight,
      documentWidth,
      itemDragging,
      isMobile,
    ],
  );

  return (
    <LayoutContext.Provider value={value}>
      {children({
        centerRef,
        centerWrapperRef,
        documentRef,
      })}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider');
  }

  return context;
};

export default LayoutContext;
